import { Injectable, signal } from '@angular/core';
import { ErrorMessage, ErrorSeverity } from '../components/error-modal/error-modal.component';

/**
 * Service for managing application errors and error modal state
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  // Signal for modal visibility
  private modalVisible = signal(false);

  // Signal for current error message
  private currentError = signal<ErrorMessage | undefined>(undefined);

  // Signal for loading state
  private loading = signal(false);

  // Getters for signals
  get isModalVisible() {
    return this.modalVisible.asReadonly();
  }

  get errorMessage() {
    return this.currentError.asReadonly();
  }

  get isLoading() {
    return this.loading.asReadonly();
  }

  /**
   * Show error modal with the given error message
   */
  showError(error: ErrorMessage): void {
    this.currentError.set(error);
    this.modalVisible.set(true);
    this.loading.set(false);
  }

  /**
   * Hide the error modal
   */
  hideError(): void {
    this.modalVisible.set(false);
    this.currentError.set(undefined);
    this.loading.set(false);
  }

  /**
   * Convenience method for showing date validation errors
   */
  showDateValidationError(
    subject: string,
    startDate: Date | string,
    endDate: Date | string,
    startDateLabel: string = 'Start Date',
    endDateLabel: string = 'End Date'
  ): void {
    const startDateStr = this.formatDate(startDate);
    const endDateStr = this.formatDate(endDate);

    const details = [
      `${startDateLabel}: ${startDateStr}`,
      `${endDateLabel}: ${endDateStr}`,
    ];

    this.showError({
      subject,
      action: 'Date Range Validation',
      message: `${endDateLabel} cannot occur before ${startDateLabel}. Please adjust your date range.`,
      severity: 'error',
      details,
    });
  }

  /**
   * Convenience method for showing validation errors
   */
  showValidationError(
    subject: string,
    action: string,
    field: string,
    message: string,
    details?: string[]
  ): void {
    this.showError({
      subject,
      action,
      message,
      severity: 'error',
      field,
      details,
    });
  }

  /**
   * Convenience method for showing warning messages
   */
  showWarning(subject: string, action: string, message: string, details?: string[]): void {
    this.showError({
      subject,
      action,
      message,
      severity: 'warning',
      details,
    });
  }

  /**
   * Convenience method for showing info messages
   */
  showInfo(subject: string, action: string, message: string, details?: string[]): void {
    this.showError({
      subject,
      action,
      message,
      severity: 'info',
      details,
    });
  }

  /**
   * Convenience method for showing success messages
   */
  showSuccess(subject: string, action: string, message: string, details?: string[]): void {
    this.showError({
      subject,
      action,
      message,
      severity: 'success',
      details,
    });
  }

  /**
   * Helper method to format dates for display
   */
  private formatDate(date: Date | string): string {
    if (typeof date === 'string') {
      return date;
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
